import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@shared-kernel/primary/shared/shadcn/ui/popover";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@shared-kernel/primary/shared/shadcn/ui/command";
import { getNormalizedString } from "@utils/utils";
import Fuse from "fuse.js";
import type { ComboboxOptions } from "./combobox";

export interface ComboboxGroup {
  heading: string;
  options: ComboboxOptions[];
}

interface Props {
  groups: ComboboxGroup[];
  selectedValues: Set<string>;
  onToggle: (value: string) => void;
  placeholder: string;
  search: {
    commandInputPlaceHolder: string;
    notFoundText: string;
  };
  disabled?: boolean;
  fuseThreshold?: number;
}

const FUSE_KEYS = ["label"] as const;

export const ComboboxGrouped = ({
  groups,
  selectedValues,
  onToggle,
  placeholder,
  search,
  disabled = false,
  fuseThreshold = 0.3,
}: Props) => {
  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const scrollableRef = useRef<HTMLDivElement>(null);

  const allOptions = useMemo(() => groups.flatMap(g => g.options), [groups]);
  const fuse = useMemo(() => new Fuse(allOptions, { keys: [...FUSE_KEYS], threshold: fuseThreshold }), [allOptions, fuseThreshold]);

  const filteredIds = useMemo(() => {
    const normalized = getNormalizedString(inputValue);
    if (!normalized) return null;
    return new Set(fuse.search(normalized).map(r => r.item.value));
  }, [fuse, inputValue]);

  const filteredGroups = useMemo(
    () =>
      groups
        .map(group => ({
          ...group,
          options: filteredIds ? group.options.filter(o => filteredIds.has(o.value)) : group.options,
        }))
        .filter(group => group.options.length > 0),
    [groups, filteredIds],
  );

  useEffect(() => {
    if (!isOpen) setInputValue("");
  }, [isOpen]);

  useEffect(() => {
    scrollableRef.current?.scrollTo(0, 0);
  }, [filteredGroups, isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <Button variant="outline" role="combobox" aria-expanded={isOpen} className="w-full justify-between">
          <span className="font-normal opacity-50">{placeholder}</span>
          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" modal>
        <Command shouldFilter={false}>
          <CommandInput placeholder={search.commandInputPlaceHolder} value={inputValue} onValueChange={setInputValue} />
          <CommandList ref={scrollableRef}>
            <CommandEmpty>{search.notFoundText}</CommandEmpty>
            {filteredGroups.map((group, groupIndex) => (
              <div key={group.heading}>
                {groupIndex > 0 && <CommandSeparator />}
                <CommandGroup heading={group.heading}>
                  {group.options.map(option => (
                    <CommandItem key={option.value} value={option.value} onSelect={() => onToggle(option.value)}>
                      <Check className={cn("mr-2 size-4", selectedValues.has(option.value) ? "opacity-100" : "opacity-0")} />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
